import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Default scenic wallpaper when no custom wallpaper is set (landscape) */
export const DEFAULT_WALLPAPER =
  'https://images.pexels.com/photos/265722/pexels-photo-265722.jpeg?auto=compress&cs=tinysrgb&w=1920';

const AtmosphereContext = React.createContext({
  backgroundImage: null,
  wallpaperImage: null,
  brideName: null,
  groomName: null,
  refresh: async () => {},
  setAtmosphere: () => {},
});

export const useAtmosphere = () => React.useContext(AtmosphereContext);

function mapAtmosphere(data) {
  return {
    backgroundImage: data.background_image || null,
    wallpaperImage: data.wallpaper_image || null,
    brideName: data.bride_name || null,
    groomName: data.groom_name || null,
  };
}

export function AtmosphereProvider({ children, mode = 'auth' }) {
  const [atmosphere, setAtmosphereState] = useState({
    backgroundImage: null,
    wallpaperImage: null,
    brideName: null,
    groomName: null,
  });

  const refresh = async () => {
    try {
      const path = mode === 'auth' ? '/api/public/atmosphere' : '/api/atmosphere';
      const { data } = await axios.get(`${API_URL}${path}`, {
        withCredentials: mode !== 'auth',
      });
      setAtmosphereState(mapAtmosphere(data));
    } catch {
      // keep
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const path = mode === 'auth' ? '/api/public/atmosphere' : '/api/atmosphere';
        const { data } = await axios.get(`${API_URL}${path}`, {
          withCredentials: mode !== 'auth',
        });
        if (!cancelled) setAtmosphereState(mapAtmosphere(data));
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  return (
    <AtmosphereContext.Provider
      value={{
        ...atmosphere,
        refresh,
        setAtmosphere: (next) =>
          setAtmosphereState((prev) => ({
            ...prev,
            ...next,
            backgroundImage:
              next.backgroundImage ?? next.background_image ?? prev.backgroundImage,
            wallpaperImage:
              next.wallpaperImage ?? next.wallpaper_image ?? prev.wallpaperImage,
          })),
      }}
    >
      {children}
    </AtmosphereContext.Provider>
  );
}

function resolveUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
}

function averageColor(data, w, h, x0, x1) {
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  const stepY = Math.max(1, Math.floor(h / 40));
  const stepX = Math.max(1, Math.floor((x1 - x0) / 8));
  for (let y = 0; y < h; y += stepY) {
    for (let x = x0; x < x1; x += stepX) {
      const i = (y * w + x) * 4;
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      n += 1;
    }
  }
  if (!n) return 'rgb(245, 240, 235)';
  return `rgb(${Math.round(r / n)}, ${Math.round(g / n)}, ${Math.round(b / n)})`;
}

/**
 * Sample left/right edge colours so letterbox sides match the photo
 * (avoids the hard “image ends / flat colour starts” look).
 */
function useWallpaperSideColors(src) {
  const [colors, setColors] = useState({
    left: 'rgb(245, 240, 235)',
    right: 'rgb(245, 240, 235)',
    isLandscape: true,
  });

  useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const isLandscape = w >= h;
        const canvas = document.createElement('canvas');
        const maxW = 120;
        const scale = Math.min(1, maxW / w);
        canvas.width = Math.max(1, Math.floor(w * scale));
        canvas.height = Math.max(1, Math.floor(h * scale));
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const band = Math.max(2, Math.floor(canvas.width * 0.08));
        const left = averageColor(data, canvas.width, canvas.height, 0, band);
        const right = averageColor(
          data,
          canvas.width,
          canvas.height,
          canvas.width - band,
          canvas.width
        );
        if (!cancelled) setColors({ left, right, isLandscape });
      } catch {
        if (!cancelled) {
          setColors({
            left: 'rgb(245, 240, 235)',
            right: 'rgb(245, 240, 235)',
            isLandscape: true,
          });
        }
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setColors({
          left: 'rgb(245, 240, 235)',
          right: 'rgb(245, 240, 235)',
          isLandscape: true,
        });
      }
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  return colors;
}

/**
 * App background:
 * - Landscape wallpaper → full-bleed cover (looks natural)
 * - Portrait wallpaper → full image (contain, no zoom) + sides painted
 *   from the photo’s own edge colours (no hard seam / wrong pink bars)
 * Couple portrait card is separate and untouched.
 */
export function AtmosphereStage({ intensity = 'app', children }) {
  const { wallpaperImage } = useAtmosphere();
  const [scrollY, setScrollY] = useState(0);

  const wallpaperSrc = resolveUrl(wallpaperImage) || DEFAULT_WALLPAPER;
  const { left, right, isLandscape } = useWallpaperSideColors(wallpaperSrc);

  useEffect(() => {
    if (intensity !== 'app') return undefined;
    const onScroll = () => setScrollY(window.scrollY || 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [intensity]);

  if (intensity === 'app') {
    const shift = Math.min(scrollY * 0.03, 24);
    return (
      <div className="relative min-h-screen overflow-x-hidden">
        <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
          {isLandscape ? (
            <div
              className="absolute inset-0 will-change-transform"
              style={{ transform: `translate3d(0, ${shift}px, 0)` }}
            >
              <img
                src={wallpaperSrc}
                alt=""
                className="h-full w-full object-cover object-center"
                draggable={false}
              />
            </div>
          ) : (
            <>
              {/* Sides = photo’s own edge colours (seamless with the image) */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(90deg, ${left} 0%, ${left} 35%, ${right} 65%, ${right} 100%)`,
                }}
              />
              {/* Soft blurred cover underneath for richer blend */}
              <div className="absolute inset-0 opacity-50">
                <img
                  src={wallpaperSrc}
                  alt=""
                  className="h-full w-full object-cover object-center scale-110 blur-2xl"
                  draggable={false}
                />
              </div>
              {/* Full photo, no zoom — edges feather into the matched sides */}
              <div
                className="absolute inset-0 will-change-transform vivah-wallpaper-feather"
                style={{ transform: `translate3d(0, ${shift}px, 0)` }}
              >
                <img
                  src={wallpaperSrc}
                  alt=""
                  className="h-full w-full object-contain object-center"
                  draggable={false}
                />
              </div>
            </>
          )}

          <div className="absolute inset-0 bg-gradient-to-b from-white/35 via-white/45 to-white/60" />
          <div className="absolute inset-0 vivah-floral opacity-20" />
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
        <img
          src={wallpaperSrc}
          alt=""
          className="h-full w-full object-cover object-center"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/35" />
      </div>
      {children}
    </div>
  );
}
